import React from 'react';
import bemify from 'bemify-js';

import { Alert, Spacer, Text } from '@zapier/design-system';

import ContentList from 'app/common/components/ContentList';
import Link from 'app/common/components/Link';

import './InstallNode.scss';
import BashCodeBlock from '../components/BashCodeBlock';
import Paragraph from '../components/Paragraph';
import StepTitle from '../components/StepTitle';

const bem = bemify('install-node');

const InstallNode = () => (
  <div className={bem()}>
    <StepTitle>Install Node.js</StepTitle>
    <Paragraph>
      The Zapier CLI is a Node.js based SDK you'll use to build and deploy a
      Zapier App to instantly connect your API to hundreds of other
      applications. This quick guide will have you coding in minutes.
    </Paragraph>
    <Text margin="0 0 1em" tag="h2" type="SectionHeader">
      Installation
    </Text>
    <Paragraph>
      An easy option to get set up with Node.js is to visit{' '}
      <Link href="https://nodejs.org/en/download/" target="_blank">
        https://nodejs.org/en/download/
      </Link>{' '}
      and download the official installer for your OS. If you're installing with
      a{' '}
      <Link
        href="https://nodejs.org/en/download/package-manager/"
        target="_blank"
      >
        package manager
      </Link>{' '}
      it's even easier.
    </Paragraph>
    <Paragraph>
      After installation, confirm that Node.js is ready to use:
    </Paragraph>
    <BashCodeBlock
      lines={[
        {
          content: 'node --version',
          isCommand: true,
        },
        {
          content: 'v14.19.1',
          isComment: true,
        },
      ]}
    />
    <Alert status="info" title="A Note on Node.js Compatibility:">
      While you can install and run the Zapier CLI using any recent version of
      Node.js, you will want to check the{' '}
      <Link
        href="https://github.com/zapier/zapier-platform/blob/master/packages/cli/README.md#requirements"
        target="_blank"
      >
        latest requirements
      </Link>{' '}
      to ensure that you are using a version that is compatible with Zapier CLI
      apps. Why? In production your code runs on AWS Lambda and presently this
      is the version our platform uses there. When the Zapier CLI builds your
      code we'll check to make sure all the features you are using will be
      supported in production. To see the differences between Node.js versions,
      visit{' '}
      <Link href="http://node.green/" target="_blank">
        node.green
      </Link>
      .
    </Alert>
    <Spacer height={22} />
    <Alert
      status="warning"
      title="Other requirements to follow along with this guided tour"
    >
      This tutorial helps you get the CLI tool installed and configured. Then
      we'll clone a complete Zapier app that you will deploy and use to create
      Zaps. This part's optional, but we think following along will get you
      familiar with the important bits of the tool as fast as possible. For the
      guided tour you'll need to:
      <ContentList kind="indented">
        <li>
          <Link href="/sign-up/" target="_blank">
            Sign up
          </Link>{' '}
          for a free Zapier account, naturally.
        </li>
        <li>
          Have{' '}
          <Link href="https://git-scm.com/" target="_blank">
            git
          </Link>{' '}
          installed.
        </li>
        <li>
          Have an account on{' '}
          <Link href="https://github.com/" target="_blank">
            GitHub.com
          </Link>
          .
        </li>
      </ContentList>
    </Alert>
  </div>
);

export default InstallNode;
